import { useCallback, useEffect, useState, useMemo } from "react";
import { useLocation } from "wouter";

import { useUser } from "../../../context/UserContext";

import {
  Announcer,
  CommandGrid,
  SummaryNotice,
  SyncNote,
  Workspace,
  Wrapper,
} from "./styles";
import Button, { ButtonTypes } from "../../../components/Button";
import { InvestmentsTypeOptions, UpdateInvestmentObject } from "../types";
import {
  useGetHoldingsQuery,
  useGetInvestmentTypesQuery,
} from "../../../services";
import { FILTER_OPTIONS } from "../../../utils/constants";
import { WindowType } from "../../../utils/types";
import { usePersistedWindow } from "../../../hooks/usePersistedWindow";
import { InvestingPageProps } from "./types";
import { Holding, taxWithheldOf } from "../../../services/holdings";
import { DropdownAction } from "../../../components/Dropdown/types";
import { EmptyState, ErrorState } from "../../../components/States";
import {
  InvestmentHero,
  LedgerHeader,
  ContextRail,
  PerformanceChart,
  Contributions,
  MarketUpdates,
  DeleteInvestmentModal,
  Holdings,
  StatusChangeModal,
  ActivityFeed,
  InvestmentSkeleton,
  InvestmentPanels,
  ValuationStrips,
  RefreshNotice,
  REFRESH_NOTICE_COPY,
} from "./components";
import {
  useInvestmentData,
  useInvestmentActions,
  useInvestmentModals,
  useAccountCashPanel,
  useBrokerageTransferPanel,
  useHoldingTradePanel,
  usePriceOverridePanel,
  useProgressPanel,
  useSourceCountryPanel,
  useTradeCorrectionPanel,
  useAccountCashCommands,
  useBrokerageTransferFlow,
  useHoldingCommands,
  useLegacyProgressPanels,
  useLedgerWorkspace,
  useLoadGeneration,
  useRetainedSection,
} from "./hooks";
import {
  isLedgerMode,
  isLinkedSettlement,
  isManualMode,
  linkedAccountNameOf,
} from "../utils";
import { priceOverrideTargetFor } from "./priceOverrideTargets";

export const InvestmentPage: React.FC<InvestingPageProps> = ({ id }) => {
  // Named to avoid shadowing the global `window`.
  const [timeWindow, setTimeWindow] = usePersistedWindow(
    "investmentTimeWindow",
    WindowType.ALL,
  );

  const { data: investmentTypesData } = useGetInvestmentTypesQuery();
  const investmentTypes = useMemo<InvestmentsTypeOptions[]>(
    () =>
      (investmentTypesData ?? []).map((type) => ({
        value: String(type.id),
        label: type.name,
      })),
    [investmentTypesData],
  );
  const [investmentType, setInvestmentType] = useState("");

  // Update investment panel state. Kept distinct from `isHoldingPanelOpen`
  // below — reusing one flag would silently couple "Record trade" to editing.
  const [isOpen, setIsOpen] = useState(false);
  const [investmentUpdate, setInvestmentUpdate] = useState(
    {} as UpdateInvestmentObject,
  );

  const [isHoldingPanelOpen, setIsHoldingPanelOpen] = useState(false);

  const { investment, isLoading, error, notFoundError, lastUpdated, refetch } =
    useInvestmentData({ id });
  const isLedger = isLedgerMode(investment.trackingMode);
  const isManual = isManualMode(investment.trackingMode);
  const isLinked = isLinkedSettlement(investment);
  const settlementAccountName = linkedAccountNameOf(investment);
  const generation = useLoadGeneration({
    investmentId: id,
    accountId: investment.accountId,
    isLedger,
  });
  const modals = useInvestmentModals();
  const progressPanel = useProgressPanel();
  const tradePanel = useHoldingTradePanel();
  const accountCashPanel = useAccountCashPanel();
  const brokerageTransferPanel = useBrokerageTransferPanel();
  const correctionPanel = useTradeCorrectionPanel();
  const priceOverridePanel = usePriceOverridePanel();
  const sourceCountryPanel = useSourceCountryPanel();
  const { user } = useUser();
  const closeHoldingPanel = useCallback(() => setIsHoldingPanelOpen(false), []);
  const closeInvestmentEdit = useCallback(() => setIsOpen(false), []);
  const handleInvestmentActionSuccess = useCallback(() => {
    modals.closeActivateModal();
    progressPanel.closePanel();
  }, [modals.closeActivateModal, progressPanel.closePanel]);
  const openInvestmentEdit = useCallback((update: UpdateInvestmentObject) => {
    setInvestmentUpdate(update);
    setIsOpen(true);
  }, []);
  const holdingCommands = useHoldingCommands({
    investmentId: id,
    accountId: investment.accountId,
    onBuyRecorded: closeHoldingPanel,
    onTradeRecorded: tradePanel.closePanel,
    correctedCommand: correctionPanel.panel.command,
    onCorrectionRecorded: correctionPanel.closePanel,
    settlementAccountName,
  });
  const accountCashCommands = useAccountCashCommands({
    investmentId: id,
    accountId: investment.accountId,
    onRecorded: accountCashPanel.closePanel,
    settlementAccountName,
  });
  const brokerageTransfer = useBrokerageTransferFlow({
    investmentId: id,
    accountId: investment.accountId,
    investmentName: investment.name,
    mode: brokerageTransferPanel.panel.mode,
    transferId: brokerageTransferPanel.panel.transferId,
    openEditPanel: brokerageTransferPanel.openEditPanel,
    closePanel: brokerageTransferPanel.closePanel,
  });
  const legacyPanels = useLegacyProgressPanels({
    openEditPanel: progressPanel.openEditPanel,
    openDuplicatePanel: progressPanel.openDuplicatePanel,
  });
  const actions = useInvestmentActions({
    id,
    investment,
    investmentTypes,
    onSuccess: handleInvestmentActionSuccess,
    onEdit: openInvestmentEdit,
    onUpdated: closeInvestmentEdit,
    panelAction: progressPanel.panel.action,
    panelProgress: progressPanel.panel.progress,
  });

  // Skip avoids a spurious 400 toast: `accountId` is undefined on the first
  // render, and `GET /holdings` requires it.
  const holdingsQuery = useGetHoldingsQuery(
    // Closed positions included: they're listed behind the Holdings card's own
    // toggle, and the dividend flow must still reach a position sold to zero.
    {
      accountId: investment.accountId,
      includeClosed: true,
      context: generation.context,
      generation: generation.generation,
    },
    { skip: !investment.accountId || !isLedger || !generation.isReady },
  );
  const holdingsSection = useRetainedSection({
    query: holdingsQuery,
    section: "holdings",
    scopeKey: String(investment.accountId),
    generation,
  });
  const holdings = holdingsSection.response?.result;
  const holdingsFailed = holdingsSection.error !== undefined;
  const refetchHoldings = holdingsQuery.refetch;

  // Stable so the memoised Holdings card is not re-rendered by its own retry.
  const retryHoldings = useCallback(async () => {
    await refetchHoldings();
  }, [refetchHoldings]);

  const { summaryRefreshFailed, isSummaryRefreshing, retrySummary } =
    generation;
  const hasSummary = generation.summary !== null;
  const summaryNoticeCopy = hasSummary
    ? REFRESH_NOTICE_COPY.summary
    : REFRESH_NOTICE_COPY.summaryUnavailable;
  const holdingsRefreshFailed = holdingsSection.refreshFailed;
  // The rail only claims kept figures it has: a never-loaded summary is the header's notice alone.
  const railSummaryFailed = summaryRefreshFailed && hasSummary;
  const railRefreshFailed =
    holdings !== undefined && (railSummaryFailed || holdingsRefreshFailed);
  const isRailRefreshing =
    (railSummaryFailed && isSummaryRefreshing) ||
    (holdingsRefreshFailed && holdingsSection.isRefreshing);
  const retryRail = useCallback(() => {
    if (railSummaryFailed) {
      void retrySummary();
    }
    if (holdingsRefreshFailed) {
      void refetchHoldings();
    }
  }, [railSummaryFailed, holdingsRefreshFailed, retrySummary, refetchHoldings]);

  // Pass the whole position (no as-of date on the row) so `priceOverrideTargetFor`
  // can offer predecessor listings instead of fixing the form to today's ticker.
  const openPriceOverrideForHolding = useCallback(
    (holding: Holding) => {
      priceOverridePanel.openPanel({
        kind: "holding",
        holding,
      });
    },
    [priceOverridePanel],
  );
  const openPriceOverrideForStrip = useCallback(
    (repairable: Holding[]) => {
      priceOverridePanel.openPanel(
        repairable.length === 1
          ? { kind: "holding", holding: repairable[0] }
          : { kind: "holdings", holdings: repairable },
      );
    },
    [priceOverridePanel],
  );
  const openPriceOverrideForBlockedDate = useCallback(
    (asOf: string) => {
      priceOverridePanel.openPanel({ kind: "blockedDate", asOf });
    },
    [priceOverridePanel],
  );
  const holdingsList = useMemo(() => holdings ?? [], [holdings]);
  // Lifted here rather than left inside `Holdings`: the rail describes the rows the table shows.
  const workspace = useLedgerWorkspace(holdings);
  // Derived, not captured: the chart is its own query, so the blocked note can be clicked while the
  // ledger is still loading. Resolving here lets those holdings fill the select when they land.
  const priceOverrideTarget = useMemo(
    () =>
      priceOverrideTargetFor(priceOverridePanel.panel.request, holdingsList),
    [priceOverridePanel.panel.request, holdingsList],
  );

  const openHoldingFlow = useCallback(() => setIsHoldingPanelOpen(true), []);

  const [, setLocation] = useLocation();

  const panelControllers = useMemo(
    () => ({
      trade: {
        panel: tradePanel.panel,
        openPanel: tradePanel.openPanel,
        closePanel: tradePanel.closePanel,
      },
      accountCash: {
        panel: accountCashPanel.panel,
        openPanel: accountCashPanel.openPanel,
        closePanel: accountCashPanel.closePanel,
      },
      brokerageTransfer: {
        panel: brokerageTransferPanel.panel,
        openCreatePanel: brokerageTransferPanel.openCreatePanel,
        openEditPanel: brokerageTransferPanel.openEditPanel,
        closePanel: brokerageTransferPanel.closePanel,
      },
      correction: {
        panel: correctionPanel.panel,
        openPanel: correctionPanel.openPanel,
        closePanel: correctionPanel.closePanel,
      },
      priceOverride: {
        panel: priceOverridePanel.panel,
        openPanel: priceOverridePanel.openPanel,
        closePanel: priceOverridePanel.closePanel,
      },
      sourceCountry: {
        panel: sourceCountryPanel.panel,
        openPanel: sourceCountryPanel.openPanel,
        closePanel: sourceCountryPanel.closePanel,
      },
      progress: {
        panel: progressPanel.panel,
        openCreatePanel: progressPanel.openCreatePanel,
        openEditPanel: progressPanel.openEditPanel,
        openDuplicatePanel: progressPanel.openDuplicatePanel,
        closePanel: progressPanel.closePanel,
        getPanelTitle: progressPanel.getPanelTitle,
      },
    }),
    [
      tradePanel.panel,
      tradePanel.openPanel,
      tradePanel.closePanel,
      accountCashPanel.panel,
      accountCashPanel.openPanel,
      accountCashPanel.closePanel,
      brokerageTransferPanel.panel,
      brokerageTransferPanel.openCreatePanel,
      brokerageTransferPanel.openEditPanel,
      brokerageTransferPanel.closePanel,
      correctionPanel.panel,
      correctionPanel.openPanel,
      correctionPanel.closePanel,
      priceOverridePanel.panel,
      priceOverridePanel.openPanel,
      priceOverridePanel.closePanel,
      sourceCountryPanel.panel,
      sourceCountryPanel.openPanel,
      sourceCountryPanel.closePanel,
      progressPanel.panel,
      progressPanel.openCreatePanel,
      progressPanel.openEditPanel,
      progressPanel.openDuplicatePanel,
      progressPanel.closePanel,
      progressPanel.getPanelTitle,
    ],
  );
  const panelCommands = useMemo(
    () => ({
      buyHolding: holdingCommands.buyHolding,
      recordHoldingTrade: holdingCommands.recordHoldingTrade,
      editTradeCommand: holdingCommands.editTradeCommand,
      recordAccountCash: accountCashCommands.recordAccountCash,
      recordBrokerageTransfer: brokerageTransfer.recordBrokerageTransfer,
      updateInvestment: actions.handleUpdateInvestment,
      submitProgress: actions.handleSubmit,
    }),
    [
      holdingCommands.buyHolding,
      holdingCommands.recordHoldingTrade,
      holdingCommands.editTradeCommand,
      accountCashCommands.recordAccountCash,
      brokerageTransfer.recordBrokerageTransfer,
      actions.handleUpdateInvestment,
      actions.handleSubmit,
    ],
  );
  const investmentEditState = useMemo(
    () => ({
      isOpen,
      investment: investmentUpdate,
      onClose: closeInvestmentEdit,
    }),
    [isOpen, investmentUpdate, closeInvestmentEdit],
  );
  const holdingEntryState = useMemo(
    () => ({ isOpen: isHoldingPanelOpen, onClose: closeHoldingPanel }),
    [isHoldingPanelOpen, closeHoldingPanel],
  );

  useEffect(() => {
    const name =
      investmentTypes.find(
        (type) => type.value === String(investment.investmentTypeId),
      )?.label ?? "";
    setInvestmentType(name);
  }, [investment.investmentTypeId, investmentTypes]);

  const dropdownActions = useMemo<DropdownAction[]>(
    () => [
      {
        id: "edit",
        label: "Edit investment",
        icon: "",
        onClick: actions.handleEdit,
        show: true,
      },
      {
        id: "deactivate",
        label: "Deactivate investment",
        icon: "",
        onClick: modals.openActivateModal,
        show: investment.isActive,
      },
      {
        id: "activate",
        label: "Reactivate investment",
        icon: "",
        onClick: modals.openActivateModal,
        show: !investment.isActive,
      },
      {
        id: "account-cash",
        label: "Record account fee or interest",
        icon: "",
        onClick: accountCashPanel.openPanel,
        // Ledger only: a manual/market-update investment has no brokerage cash to move.
        show: isLedger,
      },
      {
        id: "delete",
        label: "Delete investment",
        icon: "",
        onClick: () => {
          void (async () => {
            actions.resetDeleteError();
            await actions.fetchDeletionImpact();
            modals.openDeleteModal();
          })();
        },
        variant: "destructive" as const,
        show: true,
      },
    ],
    [
      investment,
      actions.handleEdit,
      modals,
      isLedger,
      accountCashPanel.openPanel,
    ],
  );

  if (isLoading && !investment.id) {
    return <InvestmentSkeleton />;
  }

  if ((!isLoading && notFoundError) || generation.isRemoved) {
    return (
      <Wrapper>
        <EmptyState
          title="Investment not found"
          description="The investment you're looking for doesn't exist."
          action={
            <Button
              size="large"
              type={ButtonTypes.primary}
              onClick={() => setLocation("/investments")}
              aria-label="Return to investments list"
            >
              Back to investments
            </Button>
          }
        />
      </Wrapper>
    );
  }

  if (error && !investment.id) {
    return (
      <Wrapper>
        <ErrorState message={error} onRetry={() => refetch()} />
      </Wrapper>
    );
  }

  const hasContributions = investment.contributionsCount > 0;
  // The account's own date only until the summary answers, or on a server that sends no freshness.
  const ledgerLastUpdated = generation.freshness
    ? new Date(generation.freshness.generatedAt)
    : lastUpdated;
  const isOutOfSync = isLedger && generation.consistency === "UPDATING";
  const showLegacySections = !isLedger && hasContributions;
  const taxWithheld = generation.summary
    ? taxWithheldOf(generation.summary)
    : null;
  const openSourceCountryPanel = user?.isAdmin
    ? sourceCountryPanel.openPanel
    : undefined;
  return (
    <Wrapper className="fade-in">
      <DeleteInvestmentModal
        isOpen={modals.deleteModal}
        onClose={modals.closeDeleteModal}
        onConfirm={actions.handleOnDelete}
        investmentName={investment.name}
        deletionImpact={actions.deletionImpact}
        isLoadingImpact={actions.isLoadingImpact}
        isDeleting={actions.isDeleting}
        deleteError={actions.deleteError}
      />
      <StatusChangeModal
        isOpen={modals.activateModal}
        onClose={modals.closeActivateModal}
        onConfirm={actions.handleOnStatusChangeClick}
        investment={investment}
      />

      {/* The fallback branch is every non-ledger account, not `isManual`: the one frame before
          `trackingMode` lands keeps today's hero rather than rendering nothing. */}
      {isLedger ? (
        <LedgerHeader
          investment={investment}
          summary={generation.summary}
          isEmptyAccount={holdings?.length === 0}
          lastUpdated={ledgerLastUpdated}
          dropdownActions={dropdownActions}
          onAddProgress={progressPanel.openCreatePanel}
          onRecordTrade={openHoldingFlow}
          onMoveCash={brokerageTransferPanel.openCreatePanel}
        />
      ) : (
        <InvestmentHero
          investment={investment}
          investmentType={investmentType}
          lastUpdated={lastUpdated}
          contributions={investment.contributionsCount}
          dropdownActions={dropdownActions}
          onAddProgress={progressPanel.openCreatePanel}
          onAddHolding={openHoldingFlow}
          onMoveCash={brokerageTransferPanel.openCreatePanel}
        />
      )}

      {summaryRefreshFailed && (
        <SummaryNotice>
          <RefreshNotice
            {...summaryNoticeCopy}
            isRetrying={isSummaryRefreshing}
            onRetry={() => void retrySummary()}
          />
        </SummaryNotice>
      )}

      {isLedger && (
        <ValuationStrips
          investment={investment}
          holdings={holdings}
          onSetPrice={openPriceOverrideForStrip}
        />
      )}

      {isOutOfSync && (
        <SyncNote role="status">
          Some sections are still updating, so their figures may not match each
          other yet.
        </SyncNote>
      )}

      <PerformanceChart
        id={id}
        generation={generation}
        window={timeWindow}
        options={FILTER_OPTIONS}
        onWindowChange={setTimeWindow}
        onSetPrice={isLedger ? openPriceOverrideForBlockedDate : undefined}
        taxWithheld={taxWithheld}
        showsCash={!isLinked}
      />

      {/* Ledger-gated: a MANUAL_MARKET_UPDATES investment must expose no
          trade-history correction controls, so neither section renders. */}
      {isLedger && (
        <>
          <CommandGrid>
            <Announcer role="status" aria-live="polite" aria-atomic="true">
              {workspace.selectionAnnouncement}
            </Announcer>
            <Workspace>
              <Holdings
                holdings={holdings}
                workspace={workspace}
                hasError={holdingsFailed}
                refreshFailed={holdingsRefreshFailed}
                isRefreshing={holdingsSection.isRefreshing}
                freshness={holdingsSection.response?.freshness ?? null}
                onRetry={retryHoldings}
                onTrade={tradePanel.openPanel}
                onRecordTrade={openHoldingFlow}
                onSetPrice={openPriceOverrideForHolding}
                onSetCountry={openSourceCountryPanel}
              />
            </Workspace>
            <ContextRail
              investment={investment}
              summary={generation.summary}
              selectedHolding={workspace.selectedHolding}
              onClearSelection={workspace.clearSelection}
              holdings={holdingsList}
              counts={workspace.counts}
              isLoading={holdings === undefined && !holdingsFailed}
              hasError={holdingsFailed}
              refreshFailed={railRefreshFailed}
              isRefreshing={isRailRefreshing}
              onRetry={retryRail}
            />
          </CommandGrid>
          <ActivityFeed
            accountId={investment.accountId}
            investmentId={id}
            investmentName={investment.name}
            settlementAccountName={settlementAccountName}
            holdings={holdingsList}
            onEdit={correctionPanel.openPanel}
            onEditTransfer={(transactionId) =>
              void brokerageTransfer.openBrokerageTransferEdit(transactionId)
            }
            onDeleteTransfer={brokerageTransfer.deleteBrokerageTransfer}
            generation={generation}
          />
        </>
      )}

      {showLegacySections && (
        <>
          <Contributions
            id={id}
            onChanged={refetch}
            onEdit={(item) =>
              void legacyPanels.openContributionPanel(item, "edit")
            }
            onDuplicate={(item) =>
              void legacyPanels.openContributionPanel(item, "duplicate")
            }
          />

          <MarketUpdates
            id={id}
            onChanged={refetch}
            onEdit={(item) => legacyPanels.openMarketUpdatePanel(item, "edit")}
            onDuplicate={(item) =>
              legacyPanels.openMarketUpdatePanel(item, "duplicate")
            }
          />
        </>
      )}

      <InvestmentPanels
        investmentId={id}
        investment={investment}
        isLedger={isLedger}
        isManual={isManual}
        settlementAccountName={settlementAccountName}
        holdings={holdingsList}
        priceOverrideTarget={priceOverrideTarget}
        controllers={panelControllers}
        commands={panelCommands}
        investmentEdit={investmentEditState}
        holdingEntry={holdingEntryState}
      />
    </Wrapper>
  );
};
